import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

// Shared loader for the dropdown options used by the eligibility group
// editors. Both PrimaryGroupFilters and EligibilityGroupsPanel call this so
// the location + class pickers stay in sync.

// Locations use `officeName`; classes/departments/etc. use `name`.
type OrgItem = { id: number; name?: string | null; officeName?: string | null };
type Option = { value: string; label: string };

function toOption(row: OrgItem): Option {
  const label = row.officeName?.trim() || row.name?.trim() || `#${row.id}`;
  return { value: String(row.id), label };
}

export function useOrgOptions(clientId: number | undefined): {
  locationOptions: Option[];
  classOptions: Option[];
  isLoading: boolean;
} {
  const ownerParams =
    clientId != null
      ? `?ownerType=client&ownerId=${clientId}`
      : `?ownerType=client`;

  // Mirror OrganizationManager's queryKey shape exactly so its invalidate()
  // after a create/edit/delete also invalidates this cache. Otherwise
  // ["/api/org/employee-classes", clientId] and
  // ["/api/org/employee-classes", "client", clientId] are separate entries
  // and the dropdown stays stale until a full reload.
  const { data: locations = [], isLoading: locLoading } = useQuery<OrgItem[]>({
    queryKey: ["/api/org/locations", "client", clientId],
    enabled: clientId != null,
    queryFn: async () => {
      const res = await fetch(`/api/org/locations${ownerParams}`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: classes = [], isLoading: clsLoading } = useQuery<OrgItem[]>({
    queryKey: ["/api/org/employee-classes", "client", clientId],
    enabled: clientId != null,
    queryFn: async () => {
      const res = await fetch(`/api/org/employee-classes${ownerParams}`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  const locationOptions = useMemo(() => locations.map(toOption), [locations]);
  const classOptions = useMemo(() => classes.map(toOption), [classes]);

  return { locationOptions, classOptions, isLoading: locLoading || clsLoading };
}
